import { Pick, CompactPick, SharedPickResolution, Product } from '../types';

export const MAX_PICKS = 20;

let uidCounter = 0;
function uid(prefix = 'sp'): string {
  uidCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${uidCounter.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function toBase64Url(str: string): string {
  // UTF-8 safe: use TextEncoder → bytes → btoa
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  const b64 = btoa(binary);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(str: string): string {
  const b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

export function encodePicks(picks: Pick[]): string {
  const compact: CompactPick[] = picks.slice(0, MAX_PICKS).map((p) => {
    const c: CompactPick = { p: p.product.productId };
    if (p.selections.color) c.c = p.selections.color;
    if (p.selections.compressionLevel) c.l = p.selections.compressionLevel;
    if (p.selections.style) c.y = p.selections.style;
    if (p.selections.size) c.z = p.selections.size;
    if (p.selections.gender) c.g = p.selections.gender;
    return c;
  });
  return toBase64Url(JSON.stringify(compact));
}

function isCompactPick(x: unknown): x is CompactPick {
  if (!x || typeof x !== 'object') return false;
  const r = x as Record<string, unknown>;
  // color is now optional — only productId is required
  return typeof r.p === 'string';
}

export function decodePicks(encoded: string): CompactPick[] {
  try {
    const json = fromBase64Url(encoded);
    const parsed: unknown = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isCompactPick).slice(0, MAX_PICKS);
  } catch {
    return [];
  }
}

export function resolveSharedPicks(items: CompactPick[], products: Product[]): SharedPickResolution[] {
  return items.map((raw): SharedPickResolution => {
    const product = products.find((pr) => pr.productId === raw.p);
    if (!product) {
      return { sharedId: uid(), status: 'missing', raw };
    }
    // Bare pick (no color yet) — receiver should offer to configure
    if (!raw.c) {
      return {
        sharedId: uid(),
        status: 'bare',
        raw,
        product,
        selections: {
          compressionLevel: raw.l,
          style: raw.y,
          size: raw.z,
          gender: raw.g,
        },
      };
    }
    // Products whose variants are size-specific (braces: one variant per
    // colour × size) can't be resolved without a size — never guess one.
    // Hand the pick back as 'bare' with the colour preserved so the
    // Configure flow finishes the job. Compression variants have size:null,
    // so this branch never fires for them.
    const sizeMatters = product.variants.some((v) => v.size);
    if (sizeMatters && !raw.z) {
      return {
        sharedId: uid(),
        status: 'bare',
        raw,
        product,
        selections: {
          color: raw.c,
          compressionLevel: raw.l,
          style: raw.y,
          gender: raw.g,
        },
      };
    }
    const variant = product.variants.find(
      (v) => v.color === raw.c
        && (!raw.l || v.compressionLevel === raw.l)
        && (!sizeMatters || v.size === raw.z)
    );
    if (!variant) {
      return { sharedId: uid(), status: 'invalid-variant', raw, product };
    }
    const parts: string[] = [raw.c];
    if (raw.l) parts.push(`${raw.l} mmHg`);
    if (raw.y) parts.push(raw.y);
    if (raw.z) parts.push(`Size ${raw.z}`);
    if (raw.g) parts.push(raw.g);
    const variantDescription = parts.join(', ');
    return {
      sharedId: uid(),
      status: 'ok',
      raw,
      product,
      variant,
      selections: {
        color: raw.c,
        compressionLevel: raw.l,
        style: raw.y,
        size: raw.z,
        gender: raw.g,
      },
      variantDescription,
    };
  });
}

export function buildShareUrl(picks: Pick[]): string {
  const encoded = encodePicks(picks);
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.set('picks', encoded);
  return url.toString();
}

export function clearPicksFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('picks')) return;
    url.searchParams.delete('picks');
    url.searchParams.delete('src'); // reorder-link marker rides along with ?picks=
    window.history.replaceState({}, '', url.toString());
  } catch {
    // no-op
  }
}

export function getPicksParamFromUrl(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('picks');
  } catch {
    return null;
  }
}

export function makePickId(): string {
  return uid('pk');
}
